/**
 * Support Request New Page - Create new support request (Patient)
 *
 * Mobile-friendly full page for creating a new support request.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import { supportService } from "../services/support";
import {
  relationshipService,
  type ClinicShare,
} from "../services/relationships";
import type { SupportRequestPriorityType } from "../types/support";
import { SupportRequestPriority, PRIORITY_INFO } from "../types/support";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/textarea/textarea.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/select/select.js";
import "@awesome.me/webawesome/dist/components/option/option.js";
import "../components/app-shell";

@customElement("support-request-new-page")
export class SupportRequestNewPage extends LitElement {
  @state() private clinicShares: ClinicShare[] = [];
  @state() private isLoading = true;
  @state() private isSubmitting = false;
  @state() private error = "";

  // Form state
  @state() private selectedClinic = "";
  @state() private selectedPriority: SupportRequestPriorityType =
    SupportRequestPriority.Question;
  @state() private subject = "";
  @state() private message = "";

  static styles = css`
    :host {
      display: block;
    }

    .page-content {
      padding: 1rem;
      max-width: 600px;
      margin: 0 auto;
    }

    .back-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid #e9ecef;
    }

    .back-header h1 {
      margin: 0;
      font-size: 1.25rem;
      flex: 1;
    }

    .form-group {
      margin-bottom: 1.25rem;
    }

    .form-group label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: #333;
    }

    .priority-options {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .priority-option {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      border: 2px solid #e9ecef;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: all 0.2s;
    }

    .priority-option:hover {
      background: #f8f9fa;
    }

    .priority-option.selected {
      border-color: var(--wa-color-brand-60, #0d6efd);
      background: var(--wa-color-brand-95, #f0f6ff);
    }

    .priority-option input[type="radio"] {
      width: 1.25rem;
      height: 1.25rem;
      margin: 0;
    }

    .priority-info {
      flex: 1;
    }

    .priority-label {
      font-weight: 500;
      margin-bottom: 0.125rem;
    }

    .priority-desc {
      font-size: 0.8125rem;
      color: #666;
    }

    .urgent-alert {
      margin-top: 1rem;
      padding: 1rem;
      background: #fff3e0;
      border: 1px solid #ff9800;
      border-radius: 0.5rem;
    }

    .urgent-alert h4 {
      margin: 0 0 0.5rem 0;
      color: #e65100;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1rem;
    }

    .urgent-alert p {
      margin: 0 0 0.75rem 0;
      font-size: 0.875rem;
      color: #333;
    }

    .emergency-contacts {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .emergency-contact {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    .emergency-contact a {
      color: #1565c0;
      font-weight: 600;
      text-decoration: none;
    }

    .emergency-contact a:hover {
      text-decoration: underline;
    }

    .form-actions {
      display: flex;
      gap: 0.75rem;
      margin-top: 1.5rem;
      padding-top: 1rem;
      border-top: 1px solid #e9ecef;
    }

    .form-actions wa-button {
      flex: 1;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      text-align: center;
      color: #666;
    }

    wa-input,
    wa-textarea,
    wa-select {
      width: 100%;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._loadClinics();
  }

  private async _loadClinics() {
    try {
      this.isLoading = true;
      this.clinicShares = await relationshipService.getMyClinicShares();

      // Auto-select if only one clinic
      if (this.clinicShares.length === 1) {
        this.selectedClinic = this.clinicShares[0].clinicId;
      }
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load clinics";
    } finally {
      this.isLoading = false;
    }
  }

  private async _submitRequest() {
    if (!this.selectedClinic || !this.subject.trim() || !this.message.trim()) {
      return;
    }

    try {
      this.isSubmitting = true;
      this.error = "";

      await supportService.createRequest({
        clinicGroupId: this.selectedClinic,
        priority: this.selectedPriority,
        subject: this.subject.trim(),
        message: this.message.trim(),
      });

      Router.go("/support");
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to submit request";
    } finally {
      this.isSubmitting = false;
    }
  }

  private _goBack() {
    Router.go("/support");
  }

  render() {
    return html`
      <app-shell pageTitle="New Request">
        <div class="page-content">
          <div class="back-header">
            <wa-button variant="text" size="small" @click=${this._goBack}>
              <wa-icon name="arrow-left"></wa-icon>
            </wa-button>
            <h1>New Support Request</h1>
          </div>

          ${this.error
            ? html`
                <wa-callout variant="danger" open style="margin-bottom: 1rem;">
                  <wa-icon slot="icon" name="octagon-exclamation"></wa-icon>
                  ${this.error}
                </wa-callout>
              `
            : null}
          ${this.isLoading
            ? html`
                <div class="loading-container">
                  <wa-spinner style="font-size: 2rem;"></wa-spinner>
                  <p>Loading...</p>
                </div>
              `
            : this._renderForm()}
        </div>
      </app-shell>
    `;
  }

  private _renderForm() {
    return html`
      ${this.clinicShares.length > 1
        ? html`
            <div class="form-group">
              <label>Send to Clinic</label>
              <wa-select
                value=${this.selectedClinic}
                @change=${(e: CustomEvent) =>
                  (this.selectedClinic = (e.target as HTMLSelectElement).value)}
                placeholder="Select a clinic"
              >
                ${this.clinicShares.map(
                  (cs) => html`
                    <wa-option value=${cs.clinicId}>${cs.clinicName}</wa-option>
                  `,
                )}
              </wa-select>
            </div>
          `
        : null}

      <div class="form-group">
        <label>Urgency Level</label>
        <div class="priority-options">
          ${Object.entries(PRIORITY_INFO).map(([key, info]) => {
            const priorityKey = key as SupportRequestPriorityType;
            return html`
              <div
                class="priority-option ${this.selectedPriority === priorityKey
                  ? "selected"
                  : ""}"
                @click=${() => (this.selectedPriority = priorityKey)}
              >
                <input
                  type="radio"
                  name="priority"
                  .checked=${this.selectedPriority === priorityKey}
                />
                <div class="priority-info">
                  <div class="priority-label" style="color: ${info.color}">
                    ${info.label}
                  </div>
                  <div class="priority-desc">${info.description}</div>
                </div>
              </div>
            `;
          })}
        </div>

        ${this.selectedPriority === SupportRequestPriority.Urgent
          ? html`
              <div class="urgent-alert">
                <h4>
                  <wa-icon name="triangle-exclamation"></wa-icon>
                  For Medical Emergencies
                </h4>
                <p>
                  If you or someone else is experiencing a medical emergency
                  (severe hypo, unconsciousness, DKA symptoms), please contact
                  emergency services immediately:
                </p>
                <div class="emergency-contacts">
                  <div class="emergency-contact">
                    <wa-icon name="phone"></wa-icon>
                    <strong>Emergency:</strong>
                    <a href="tel:111">111</a> (Ambulance)
                  </div>
                  <div class="emergency-contact">
                    <wa-icon name="phone"></wa-icon>
                    <strong>Healthline:</strong>
                    <a href="tel:0800611116">0800 611 116</a> (24/7 health
                    advice)
                  </div>
                </div>
                <p style="margin-top: 0.75rem; margin-bottom: 0;">
                  You can still submit this request for clinic follow-up after
                  receiving emergency care.
                </p>
              </div>
            `
          : null}
      </div>

      <div class="form-group">
        <label>Subject</label>
        <wa-input
          placeholder="Brief summary of your question"
          .value=${this.subject}
          @input=${(e: CustomEvent) =>
            (this.subject = (e.target as HTMLInputElement).value)}
        ></wa-input>
      </div>

      <div class="form-group">
        <label>Message</label>
        <wa-textarea
          placeholder="Describe your question or concern in detail..."
          rows="5"
          .value=${this.message}
          @input=${(e: CustomEvent) =>
            (this.message = (e.target as HTMLInputElement).value)}
        ></wa-textarea>
      </div>

      <div class="form-actions">
        <wa-button
          variant="neutral"
          @click=${this._goBack}
          ?disabled=${this.isSubmitting}
        >
          Cancel
        </wa-button>
        <wa-button
          variant="brand"
          @click=${this._submitRequest}
          ?loading=${this.isSubmitting}
          ?disabled=${!this.selectedClinic ||
          !this.subject.trim() ||
          !this.message.trim()}
        >
          Submit Request
        </wa-button>
      </div>
    `;
  }
}
